import React, { useState, useEffect } from 'react';
import { saveAs } from 'file-saver';
import uniqid from 'uniqid';
import classNames from 'classnames';

import Loader from 'components/partials/loader/loader';
import Breadcrumbs from 'components/partials/beadcrumbs/breadcrumbs';
import Button from 'components/partials/button/button';
import RelatedContent from 'containers/user/learn/components/related-content/related-content';

import api from 'api';

import { useCurrentUser } from 'state/current-user/current-user.thunk';

import {
  getIsRelatedContentShown,
  getRelatedContent,
} from 'containers/user/learn/heplers/content-builder-helpers';
import MarkdownComponent from 'containers/user/learn/components/markdown/markdown';
import { getBlocksFromContent } from 'containers/user/learn/heplers/get-blocks-from-content';

import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';
import Heading from 'components/partials/heading/heading';

import { formatDate } from 'helpers/dates-formatter';
import Icons from 'assets/icons';

interface DownloadFileEntry {
  attributes?: { name?: string; url?: string };
}

interface DownloadData {
  name?: string;
  heroDescription?: string;
  downloadText?: string;
  downloadBtnText?: string;
  assetCreationDate?: string;
  heroImage?: { data?: { attributes?: { url?: string } } };
  downloadFile?: { data: DownloadFileEntry[] };
  content?: Array<{ id: string | number; type: string; [key: string]: unknown }>;
  [key: string]: unknown;
}

interface DownloadProps {
  downloadId: string | number;
}

const Download = ({ downloadId }: DownloadProps): React.JSX.Element => {
  const [download, setDownload] = useState<DownloadData>({});
  const [loading, setLoading] = useState<boolean>(false);

  const user = useCurrentUser();

  const dowloadFile = (): void => {
    setLoading(true);
    const fileName = download?.downloadFile?.data[0]?.attributes?.name;
    const fileUrl = download?.downloadFile?.data[0]?.attributes?.url;

    saveAs(fileUrl as string, fileName);

    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    api
      .getDownload(downloadId, user?.data?.oktaUserId ?? '')
      .then((res) => {
        setDownload(
          (res as { data: { data: { attributes: DownloadData } } }).data.data.attributes,
        );
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const isDatePresented = !!download?.assetCreationDate;

  return !!loading ? (
    <Loader {...{ loading }} />
  ) : (
    <div className="flex flex-col justify-center w-full">
      <div className="flex justify-center w-full h-auto">
        <ResponsiveWrapper customClass="flex flex-col justify-center !w-auto">
          <Breadcrumbs
            breadcrumbs={[
              { label: 'Learn', href: '/learn' },
              { label: 'Downloads', href: '/learn/content/downloads' },
            ]}
            customClass="!mt-5"
          />
          <div className="flex items-start mobile:flex-col tablet:flex-row gap-6 mobile:py-4 tablet:py-10">
            <div className="flex flex-col">
              <Heading
                customClass={classNames('text-4xl font-semibold mb-2', {
                  'mobile:mb-6 xs:mb-8': !isDatePresented,
                })}
              >
                {download?.name}
              </Heading>

              {isDatePresented && (
                <div className="flex items-center text-navy-500 mobile:mb-6 xs:mb-8">
                  <Icons.Calendar className="h-4 w-4 mr-1" />
                  <div className="text-sm">
                    {formatDate(download.assetCreationDate as string, 'MMM D, YYYY')}
                  </div>
                </div>
              )}

              <MarkdownComponent text={download?.heroDescription} />

              <span className="font-large font-semibold mobile:mt-6 md:mt-10">
                {download?.downloadText}
              </span>
              <Button
                variant="brand-filled"
                size="sm"
                iconAfter={<Icons.Download />}
                className="mt-3 w-fit"
                onClick={() => dowloadFile()}
              >
                {download?.downloadBtnText}
              </Button>
            </div>
            {download?.heroImage && (
              <img
                src={download?.heroImage?.data?.attributes?.url}
                alt="Hero Img"
                className="flex h-auto tablet:w-1/2 xs:w-1/3 justify-center"
              />
            )}
          </div>
        </ResponsiveWrapper>
      </div>
      {!!download.content?.length && (
        <div className="flex justify-center w-full">
          <ResponsiveWrapper
            customClass="flex flex-col justify-center my-3"
          >
            {download.content.map((component) =>
              getBlocksFromContent({ ...component, key: uniqid() })
            )}
            {getIsRelatedContentShown(download as never, 'downloadIsLinkedTo') && (
              <RelatedContent
                content={getRelatedContent(download as never, 'downloadIsLinkedTo') as never}
              />
            )}
          </ResponsiveWrapper>
        </div>
      )}
    </div>
  );
};

export default Download;
